import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useScrollSentinel } from '@/hooks/useScrollSentinel';
import { format, isToday, isYesterday, isSameWeek, startOfWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, Headphones, Shield, MessageSquare, AlertCircle } from 'lucide-react';
import { useConversationMessagesInfinite } from '@/hooks/useConversationMessagesInfinite';
import { useLeadAdPreviews } from '@/hooks/useLeadAdPreviews';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ImageModal } from './ImageModal';
import { ImagePreview } from './ImagePreview';
import { DocumentPreview } from './DocumentPreview';
import { VideoPreview } from './VideoPreview';
import { formatWhatsAppText, extractUrls } from './utils/formatWhatsAppText';
import { LinkPreview } from './LinkPreview';
import { ChatMessageBubble } from './ChatMessageBubble';
import type { ChatMessage } from '@/features/chat/messages/chatMessageTypes';
import './ChatWindow.css';

/**
 * 💬 Componente de Janela de Chat - VERSÃO SIMPLIFICADA QUE FUNCIONA
 *
 * 📋 FUNCIONALIDADES:
 * - Exibe histórico de mensagens entre usuário e lead
 * - Suporta diferentes tipos de mídia (texto, imagem, áudio)
 * - Adapta-se automaticamente para modo admin
 * - Scroll funcional garantido
 * - Exibe separadores de data para organizar conversas
 *
 * 🔧 VERSÃO ULTRA SIMPLIFICADA:
 * - Altura fixa em pixels
 * - Scroll sempre visível
 * - CSS inline para garantir funcionamento
 *
 * Design system Gens (07/10/2026): fundo branco, bolhas musgo/lima, separador de data em pílula papel,
 * estados vazio/erro no padrão 9.16 e faixa de admin em tom informativo.
 */

/** Faixa que avisa o modo administrador; tom informativo para não competir com a ação lima. */
const FaixaModoAdmin = () => (
  <div className="flex items-center gap-2 border-b border-border bg-info-subtle px-4 py-1.5 text-xs font-medium text-info-text">
    <Shield className="h-3.5 w-3.5" aria-hidden="true" />
    Visualizando como administrador
  </div>
);

interface ChatWindowProps {
  leadId: string | null;
  clinicaId: string | null;
  isAdminMode?: boolean;
}

// Define quais remetentes devem ser exibidos como "mensagem enviada" no CRM.
// Motivo: o banco pode armazenar `enviado_por` com variações ("usuario", "ia", "clinica"),
// e o frontend precisa manter o alinhamento visual correto para todos esses casos.
const REMETENTES_ENVIADOS = new Set(['usuario', 'ia', 'clinica']);

// Normaliza a regra de renderização da bolha para evitar divergência entre modo admin e padrão.
const isMensagemEnviadaPelaClinica = (enviadoPor: unknown): boolean => {
  if (typeof enviadoPor !== 'string') {
    return false;
  }

  return REMETENTES_ENVIADOS.has(enviadoPor.toLowerCase());
};

export const ChatWindow = ({
  leadId,
  clinicaId,
  isAdminMode = false,
}: ChatWindowProps) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const conteudoMensagensRef = useRef<HTMLDivElement>(null);
  // Troca de conversa precisa zerar o alinhamento antes da pintura.
  // Se isso ficar só no efeito, a conversa em cache nasce com o histórico antigo liberado
  // e o scroll para no meio, longe da mensagem mais recente.
  const conversaAlinhadaRef = useRef<string | null>(null);
  const usuarioInterrompeuAlinhamentoRef = useRef(false);
  // Só libera o carregamento do histórico antigo depois do alinhamento inicial no fim.
  const [rolagemInicialFeita, setRolagemInicialFeita] = useState(false);
  const chaveConversa = `${clinicaId ?? ''}:${leadId ?? ''}`;
  if (conversaAlinhadaRef.current !== chaveConversa) {
    conversaAlinhadaRef.current = chaveConversa;
    usuarioInterrompeuAlinhamentoRef.current = false;
    if (rolagemInicialFeita) {
      setRolagemInicialFeita(false);
    }
  }

  // 🖼️ ESTADO PARA MODAL DE IMAGEM
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageOriginRect, setSelectedImageOriginRect] = useState<DOMRect | null>(null);

  // 🎯 FUNÇÕES DE CONTROLE DO MODAL
  const handleImageClick = useCallback((imageUrl: string, sourceElement: HTMLImageElement) => {
    // Guarda a posição da miniatura para a animação de abrir/fechar não "pular" na tela.
    setSelectedImageOriginRect(sourceElement.getBoundingClientRect());
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  }, []);

  const closeImageModal = () => {
    // Fecha visualmente primeiro e limpa estado só após o fim da animação.
    setIsImageModalOpen(false);
  };

  const {
    messages,
    isPending: isLoading,
    isFetching,
    isError,
    error,
    refetch,
    fetchOlderMessages,
    hasOlderMessages,
    isFetchingOlderMessages,
  } = useConversationMessagesInfinite({ clinicaId, leadId });
  const ultimaMensagemId = messages[messages.length - 1]?.id ?? null;
  const { data: anunciosPorMensagem } = useLeadAdPreviews(clinicaId, leadId, ultimaMensagemId);

  /**
   * 📅 Gerar separador de data baseado na data da mensagem
   */
  const getDateSeparatorText = useCallback((date: Date): string => {
    if (isToday(date)) {
      return 'Hoje';
    }
    if (isYesterday(date)) {
      return 'Ontem';
    }

    // Verifica se é da semana atual
    const weekStart = startOfWeek(new Date(), {
      weekStartsOn: 1
    }); // Segunda-feira como início
    if (isSameWeek(date, new Date(), {
      weekStartsOn: 1
    }) && date >= weekStart) {
      return format(date, 'EEEE', {
        locale: ptBR
      }); // Nome do dia da semana
    }

    // Data completa para mensagens antigas
    return format(date, 'dd/MM/yyyy', {
      locale: ptBR
    });
  }, []);


  // As subscriptions ficam no hook paginado para manter somente a conversa aberta sincronizada.

  /**
   * Leva a conversa até a mensagem mais recente.
   * Os grupos fora da tela usam altura estimada (content-visibility). Um único salto
   * para nessa estimativa; quando o dia mais recente ganha a altura real, a última
   * mensagem fica abaixo da dobra. Por isso a abertura acompanha o crescimento
   * até a posição estabilizar.
   */
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    const distanciaDoFim = () => container.scrollHeight - container.scrollTop - container.clientHeight;

    if (rolagemInicialFeita) {
      // Mensagem nova só acompanha quem já está lendo o fim, para não puxar quem subiu no histórico.
      const distancia = distanciaDoFim();
      if (distancia > 1 && distancia < 150) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
      return;
    }

    const irParaMensagemMaisRecente = () => {
      container.scrollTop = container.scrollHeight;
    };

    irParaMensagemMaisRecente();

    let quadro = 0;
    let framesEstaveis = 0;
    let tentativas = 0;
    const conteudo = conteudoMensagensRef.current;
    const observador = conteudo && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        if (usuarioInterrompeuAlinhamentoRef.current) return;
        irParaMensagemMaisRecente();
        framesEstaveis = 0;
      })
      : null;
    observador?.observe(conteudo as HTMLDivElement);

    const concluirAlinhamento = () => {
      observador?.disconnect();
      setRolagemInicialFeita(true);
    };

    const acompanharAteEstabilizar = () => {
      if (usuarioInterrompeuAlinhamentoRef.current) {
        concluirAlinhamento();
        return;
      }

      // Painel ainda sem altura (oculto) não conta como fim da conversa.
      if (container.clientHeight === 0) {
        tentativas += 1;
        if (tentativas > 180) return;
        quadro = requestAnimationFrame(acompanharAteEstabilizar);
        return;
      }

      irParaMensagemMaisRecente();
      tentativas += 1;
      framesEstaveis = distanciaDoFim() <= 1 ? framesEstaveis + 1 : 0;

      // Dois quadros seguidos no fim significam que a altura real já substituiu a estimativa.
      // O limite evita laço infinito se alguma mídia continuar mudando de tamanho.
      if (framesEstaveis >= 2 || tentativas > 180) {
        concluirAlinhamento();
        return;
      }

      quadro = requestAnimationFrame(acompanharAteEstabilizar);
    };

    const interromperSeUsuarioSubir = (evento: WheelEvent) => {
      if (evento.deltaY < 0) usuarioInterrompeuAlinhamentoRef.current = true;
    };
    let toqueInicial = 0;
    const registrarToque = (evento: TouchEvent) => {
      toqueInicial = evento.touches[0]?.clientY ?? 0;
    };
    const interromperSeArrastarParaBaixo = (evento: TouchEvent) => {
      const posicaoAtual = evento.touches[0]?.clientY ?? toqueInicial;
      if (posicaoAtual - toqueInicial > 12) usuarioInterrompeuAlinhamentoRef.current = true;
    };

    container.addEventListener('wheel', interromperSeUsuarioSubir, { passive: true });
    container.addEventListener('touchstart', registrarToque, { passive: true });
    container.addEventListener('touchmove', interromperSeArrastarParaBaixo, { passive: true });
    quadro = requestAnimationFrame(acompanharAteEstabilizar);

    return () => {
      cancelAnimationFrame(quadro);
      observador?.disconnect();
      container.removeEventListener('wheel', interromperSeUsuarioSubir);
      container.removeEventListener('touchstart', registrarToque);
      container.removeEventListener('touchmove', interromperSeArrastarParaBaixo);
    };
  }, [chaveConversa, messages.length, rolagemInicialFeita, ultimaMensagemId]);

  /**
   * 🕐 Formatar Horário das Mensagens
   */
  const formatMessageTime = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    if (diffInHours < 24) {
      return format(date, 'HH:mm', {
        locale: ptBR
      });
    } else if (diffInHours < 24 * 7) {
      return format(date, 'EEE HH:mm', {
        locale: ptBR
      });
    } else {
      return format(date, 'dd/MM HH:mm', {
        locale: ptBR
      });
    }
  }, []);


  /**
   * 🎨 Renderizar Conteúdo da Mensagem por Tipo
   */
  const renderMessageContent = useCallback((mensagem: ChatMessage) => {
    const {
      anexo_url,
      conteudo,
      tipo,
      nome_arquivo,
      enviado_por
    } = mensagem;

    // Usa uma regra centralizada para tratar IA e outros remetentes internos como "enviados".
    const isUser = isMensagemEnviadaPelaClinica(enviado_por);

    // Normalizar tipos (aceitar PT e EN)
    const tipoNormalizado = tipo?.toLowerCase();
    const isImage = tipoNormalizado === 'imagem' || tipoNormalizado === 'image';
    const isAudio = tipoNormalizado === 'audio' || tipoNormalizado === 'áudio';
    const isDocument = tipoNormalizado === 'arquivo' || tipoNormalizado === 'file' || tipoNormalizado === 'document';
    const isVideo = tipoNormalizado === 'video' || tipoNormalizado === 'vídeo';

    if (isImage) {
      return (
        <div className="space-y-2 overflow-hidden">
          <ImagePreview
            imageUrl={anexo_url}
            fileName={nome_arquivo || 'Imagem enviada'}
            isLoading={!anexo_url} // Mostrar "Enviando..." se não tem URL
            onOpen={(sourceElement) => anexo_url && handleImageClick(anexo_url, sourceElement)}
          />
          {conteudo && conteudo !== 'Imagem enviada' && <p className="text-sm">{conteudo}</p>}
        </div>
      );
    }

    if (isAudio) {
      return (
        <div className="space-y-2 overflow-hidden">
          {anexo_url ? (
            <div className="chat-audio-card flex max-w-full flex-col gap-2 rounded-xl bg-card/70 p-3">
              <div className="flex items-center gap-2 text-accent-foreground">
                <Headphones className="h-4 w-4" aria-hidden="true" />
                <p className="text-xs font-semibold">Áudio</p>
              </div>
              {/* Mantém o reprodutor ampliado sem ultrapassar a largura disponível da conversa. */}
              <audio controls preload="metadata" className="chat-audio-player h-8 opacity-90 hover:opacity-100 transition-opacity">
                <source src={anexo_url} type="audio/mpeg" />
                <source src={anexo_url} type="audio/wav" />
                <source src={anexo_url} type="audio/ogg" />
                Seu navegador não suporta reprodução de áudio.
              </audio>
            </div>
          ) : (
            <div className="flex max-w-[70vw] items-center gap-2 rounded-xl bg-card/70 p-3 sm:max-w-sm" role="status">
              <div className="h-3 w-3 animate-pulse rounded-full bg-muted-foreground/50 motion-reduce:animate-none"></div>
              <span className="text-sm text-muted-foreground">Processando áudio...</span>
            </div>
          )}
          {conteudo && conteudo !== 'Áudio enviado' && <p className="text-sm">{conteudo}</p>}
        </div>
      );
    }

    if (isDocument) {
      return (
        <div className="space-y-2">
          <DocumentPreview
            documentUrl={anexo_url}
            fileName={nome_arquivo || 'Arquivo'}
            isLoading={!anexo_url}
            isUser={isUser}
          />
          {conteudo && <p className="text-sm">{conteudo}</p>}
        </div>
      );
    }

    if (isVideo) {
      return (
        <div className="space-y-2 overflow-hidden">
          <VideoPreview
            videoUrl={anexo_url}
            fileName={nome_arquivo || 'Vídeo enviado'}
            isLoading={!anexo_url} // Mostrar "Processando..." se não tem URL
          />
          {conteudo && conteudo !== 'Vídeo enviado' && <p className="text-sm">{conteudo}</p>}
        </div>
      );
    }

    // Fallback para tipos não reconhecidos (incluindo 'texto')
    // Aplica formatação WhatsApp: *negrito*, _itálico_, ~riscado~, quebras de linha
    const urls = extractUrls(conteudo || '');
    return (
      <div>
        <p className="text-sm whitespace-pre-wrap leading-relaxed break-words max-w-full overflow-hidden">
          {formatWhatsAppText(conteudo)}
        </p>
        {urls.length > 0 && (
          <LinkPreview url={urls[0]} isUserMessage={isUser} />
        )}
      </div>
    );
  }, [handleImageClick]);

  const carregarMensagensAntigas = async () => {
    const container = messagesContainerRef.current;

    // Busca no topo para preservar o fluxo natural de leitura de conversas antigas.
    if (!container || container.scrollTop > 80 || !hasOlderMessages || isFetchingOlderMessages) return;

    const alturaAnterior = container.scrollHeight;
    await fetchOlderMessages();
    requestAnimationFrame(() => {
      const novaAltura = container.scrollHeight;
      container.scrollTop += novaAltura - alturaAnterior;
    });
  };

  // IntersectionObserver evita medir o scroll a cada pixel e mantém a paginação econômica no dock.
  const sentinelaTopoRef = useScrollSentinel({
    ativo: Boolean(leadId) && rolagemInicialFeita && hasOlderMessages && !isFetchingOlderMessages,
    aoFicarVisivel: carregarMensagensAntigas,
    raizRef: messagesContainerRef,
    margemRaiz: '80px 0px',
    chaveMontagem: `${leadId ?? ''}-${isLoading}-${messages.length}`,
  });

  // 📋 Agrupar mensagens por data (para sticky header por grupo)
  const dateGroups = useMemo(() => {
    if (!messages || messages.length === 0) return [];
    const groups: { separator: { id: string; dateText: string; date: string }; messages: ChatMessage[] }[] = [];
    let lastDate: string | null = null;
    messages.forEach((message) => {
      const messageDate = new Date(message.created_at);
      const currentDateString = format(messageDate, 'yyyy-MM-dd');
      if (lastDate !== currentDateString) {
        groups.push({
          separator: {
            id: `separator-${currentDateString}`,
            dateText: getDateSeparatorText(messageDate),
            date: currentDateString,
          },
          messages: [],
        });
        lastDate = currentDateString;
      }
      groups[groups.length - 1].messages.push(message);
    });
    return groups;
  }, [getDateSeparatorText, messages]);

  // 🚫 ESTADO: Nenhum Lead Selecionado
  if (!leadId) {
    return <div className="flex-1 flex items-center justify-center chat-bg-pattern">
      <div className="text-center text-muted-foreground">
        <p>Selecione uma conversa para visualizar as mensagens</p>
      </div>
    </div>;
  }

  // ⏳ SKELETON DE CARREGAMENTO (apenas para primeiros carregamentos ou quando não há cache)
  const MessageSkeleton = () => (
    <div className="flex flex-col gap-3 p-4 md:p-6" role="status" aria-label="Carregando mensagens">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex">
          <div className="chat-bolha--recebida max-w-[70%] p-3.5">
            <Skeleton className="mb-2 h-3.5 w-48 bg-card/80" />
            <Skeleton className="h-2.5 w-12 bg-card/80" />
          </div>
        </div>
      ))}

      {[1, 2].map(i => (
        <div key={`user-${i}`} className="flex justify-end">
          <div className="chat-bolha--enviada max-w-[70%] p-3.5">
            <Skeleton className="mb-2 h-3.5 w-32 bg-card/80" />
            <Skeleton className="ml-auto h-2.5 w-10 bg-card/80" />
          </div>
        </div>
      ))}
    </div>
  );

  // ⏳ ESTADO: Primeira vez carregando (sem cache) E sem mensagens ainda
  // Aplica tanto para admin quanto usuário normal
  if (isLoading && messages.length === 0) {
    return (
      <div className="flex flex-col h-full chat-bg-pattern">
        {isAdminMode && <FaixaModoAdmin />}

        <div className="flex-1 overflow-hidden">
          <MessageSkeleton />
        </div>
      </div>
    );
  }

  // Erro de carga: antes a UI caía no "Nenhuma mensagem", escondendo falhas da RPC.
  if (isError && messages.length === 0) {
    return (
      <div className="flex flex-col h-full chat-bg-pattern">
        {isAdminMode && <FaixaModoAdmin />}
        <div className="flex flex-1 items-center justify-center p-8 text-center" role="alert">
          <div className="flex max-w-sm flex-col items-center animate-in fade-in-0 duration-300 motion-reduce:animate-none">
            <div className="lg-pontos mb-4 rounded-2xl p-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-destructive/30 bg-card text-danger-text shadow-sm">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </div>
            </div>
            <p className="text-base font-semibold text-foreground">Não foi possível carregar as mensagens</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'Tente novamente em instantes.'}
            </p>
            <Button
              type="button"
              onClick={() => void refetch()}
              className="mt-4 rounded-full font-semibold hover:bg-brand-soft active:scale-[0.98]"
            >
              Tentar de novo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 🎨 RENDERIZAÇÃO PRINCIPAL DO COMPONENTE
  return (
    <div className="flex flex-col h-full chat-bg-pattern">
      {/* 🛡️ Header Modo Admin */}
      {isAdminMode && <FaixaModoAdmin />}

      {/* 📋 Área de Mensagens */}
      <div
        key={chaveConversa}
        ref={messagesContainerRef}
        className="crm-scroll crm-scroll-fade-bottom flex-1 min-h-0 relative px-4 md:px-6 lg:px-8"
      >
        {/* Marcador no topo: carrega histórico sem observar cada evento de rolagem. */}
        <div ref={sentinelaTopoRef} className="h-px w-full" aria-hidden />
        {isFetchingOlderMessages && (
          <div className="py-3 flex justify-center text-xs text-muted-foreground" role="status">
            <Loader2 className="w-3 h-3 animate-spin mr-2 motion-reduce:animate-none" aria-hidden="true" /> Carregando mensagens anteriores...
          </div>
        )}
        {/* 🎯 Indicador de carregamento discreto no topo */}
        {(isFetching && !isFetchingOlderMessages && messages.length > 0) && (
          <div className="absolute top-0 left-0 right-0 py-1 z-10 flex justify-center pointer-events-none">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card/95 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none" role="status">
              <Loader2 className="w-3 h-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              {isAdminMode ? 'Sincronizando...' : 'Atualizando...'}
            </div>
          </div>
        )}

        {dateGroups.length === 0 ? (
          // 📝 Estado Vazio
          <div className="flex h-full flex-col items-center justify-center p-8 text-center animate-in fade-in-0 duration-300 motion-reduce:animate-none">
            <div className="lg-pontos mb-4 rounded-2xl p-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-card text-accent-foreground shadow-sm">
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
              </div>
            </div>
            <p className="text-base font-semibold text-foreground">Nenhuma mensagem ainda</p>
            <p className="mt-1 text-sm text-muted-foreground">Escreva abaixo para começar a conversa.</p>
          </div>
        ) : (
          // 💬 Grupos de mensagens por data — header sticky por grupo
          <div ref={conteudoMensagensRef} className="flex flex-col">
            {dateGroups.map((group) => (
              <div key={group.separator.id} className="chat-message-group">
                {/* 📅 Header de data: sticky dentro do grupo */}
                <div className="sticky top-0 z-10 flex justify-center py-3">
                  {/* Separador de data fixo em pílula papel com borda (9.11). */}
                  <span className="rounded-full border border-border inline-block bg-muted/95 px-3 py-1 text-[11px] font-semibold text-muted-foreground shadow-sm first-letter:uppercase">
                    {group.separator.dateText}
                  </span>
                </div>

                {/* 💬 Mensagens do grupo */}
                <div className="flex flex-col gap-2 pb-2">
                  {group.messages.map((item, index) => {
                    // Mantém a mesma regra de remetente no layout principal das bolhas.
                    const isUser = isMensagemEnviadaPelaClinica(item.enviado_por);
                    const nextMessage = group.messages[index + 1];
                    const isNextFromSame = nextMessage && nextMessage.enviado_por === item.enviado_por;
                    const adPreview = !isUser && item.id_message_whats
                      ? anunciosPorMensagem?.get(item.id_message_whats)
                      : undefined;

                    return (
                      <ChatMessageBubble
                        key={item.id}
                        message={item}
                        isUser={isUser}
                        isNextFromSameSender={Boolean(isNextFromSame)}
                        formatMessageTime={formatMessageTime}
                        renderMessageContent={renderMessageContent}
                        adPreview={adPreview}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="h-2" />
          </div>
        )}
      </div>

      {/* 🖼️ Modal de Visualização de Imagem */}
      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage}
          isOpen={isImageModalOpen}
          onClose={closeImageModal}
          originRect={selectedImageOriginRect}
          onAfterClose={() => {
            setSelectedImage(null);
            setSelectedImageOriginRect(null);
          }}
        />
      )}
    </div>
  );
};
